import { Link, useLocation } from "react-router-dom";
import { Badge, Body1, makeStyles, Subtitle2, tokens } from "@fluentui/react-components";
import { ReactNode } from "react";
import type { Role } from "../types/models";
import { NotificationsBell } from "./NotificationsBell";

const useStyles = makeStyles({
  root: {
    display: "grid",
    gridTemplateColumns: "240px 1fr",
    gridTemplateRows: "56px 1fr",
    height: "100vh",
    minHeight: 0,
    fontFamily: tokens.fontFamilyBase,
    color: tokens.colorNeutralForeground1,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  header: {
    gridColumn: "1 / span 2",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingLeft: "16px",
    paddingRight: "16px",
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  side: {
    paddingTop: "12px",
    backgroundColor: tokens.colorNeutralBackground1,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    overflowY: "auto",
  },
  main: { padding: "20px", overflowY: "auto" },
  navLink: {
    display: "block",
    padding: "8px 16px",
    color: tokens.colorNeutralForeground1,
    textDecoration: "none",
  },
  navLinkActive: {
    backgroundColor: tokens.colorNeutralBackground3,
    fontWeight: 600,
  },
  navSectionLabel: {
    padding: "16px 16px 4px 16px",
    color: tokens.colorNeutralForeground3,
    fontSize: "12px",
    textTransform: "uppercase",
  },
});

interface NavItem { label: string; to: string; }

const USER_NAV: NavItem[] = [
  { label: "My tickets", to: "/my-tickets" },
  { label: "New ticket", to: "/new" },
  { label: "Search", to: "/search" },
];
const AGENT_NAV: NavItem[] = [
  { label: "Agent dashboard", to: "/agent" },
  { label: "Queue", to: "/agent/queue" },
];
const ADMIN_NAV: NavItem[] = [
  { label: "Admin dashboard", to: "/admin" },
  { label: "All tickets", to: "/admin/tickets" },
  { label: "Users", to: "/admin/users" },
  { label: "Categories", to: "/admin/categories" },
  { label: "Queues", to: "/admin/queues" },
  { label: "SLAs", to: "/admin/slas" },
  { label: "Reports", to: "/admin/reports" },
];

export function AppShell(props: { role: Role; displayName: string; children: ReactNode }) {
  const s = useStyles();
  const loc = useLocation();

  const sections: Array<{ label: string; items: NavItem[] }> = [{ label: "Me", items: USER_NAV }];
  if (props.role === "Agent" || props.role === "Admin") sections.push({ label: "Agent", items: AGENT_NAV });
  if (props.role === "Admin") sections.push({ label: "Admin", items: ADMIN_NAV });

  return (
    <div className={s.root}>
      <div className={s.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img
            src="/sparsh-logo.png"
            alt="Sparsh"
            style={{ height: 28, width: "auto", display: "block" }}
          />
          <Subtitle2>Sparsh-IT Helpdesk</Subtitle2>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <NotificationsBell />
          <Body1>{props.displayName}</Body1>
          <Badge appearance="tint" color={props.role === "Admin" ? "important" : props.role === "Agent" ? "brand" : "informative"}>
            {props.role}
          </Badge>
        </div>
      </div>
      <nav className={s.side}>
        {sections.map((sec) => (
          <div key={sec.label}>
            <div className={s.navSectionLabel}>{sec.label}</div>
            {sec.items.map((it) => (
              <Link
                key={it.to}
                to={it.to}
                className={`${s.navLink} ${loc.pathname === it.to ? s.navLinkActive : ""}`}
              >
                {it.label}
              </Link>
            ))}
          </div>
        ))}
      </nav>
      <main className={s.main}>{props.children}</main>
    </div>
  );
}
